/*
	THIS BLOCKCHAIN DOES NOT FETCH ANYTHING - it only broadcasts & loads from the cache
	it can be used as a fallback when the blockchain that fetches doesn't work
	and we still need to be able to broadcast transactions
*/

import { is_string, is_hex } from "../helpers/helpers.js"
import { getLog, isElectron } from "../helpers/helpers.js"
import TokkenrBlockchain from "./TokkenrBlockchain.js"

//for loading RUN through webpack externals 
const Run = require("runlib")
const retry = require("async-retry")
// const pTimeout = require('p-timeout')
const promiseAny = require('p-any')
// const BsvBroadcast = require('bsv-broadcast').default
const BsvBroadcast = require('@runcraft-bitcoin/bsv-broadcast').default



const isDevMode = !isElectron()

// -----------------------------------

const NOFETCHBLOCKCHAIN_CLASSNAME = "NoFetchBlockchain"

/**
 * usage: isNoFetchBlockchain(run.blockchain)
 * @param {blockchain} run_blockchain 
 */
export function isNoFetchBlockchain(run_blockchain) {
	const function_id = "isNoFetchBlockchain"
	try {
		const log = getLog(function_id)
		if (!run_blockchain) throw new Error("must provide run_blockchain")

		// for some reason this is returning true even when it's not the case... so we can't use it
		// (PS keep this block to avoid doing that error again)
		// const is_nofetchblockchain = (run_blockchain.blockchain instanceof NoFetchBlockchain)

		let blockchain_classname = "unknown"
		if (run_blockchain.classname) blockchain_classname = run_blockchain.classname
		const is_nofetchblockchain = blockchain_classname.includes(NOFETCHBLOCKCHAIN_CLASSNAME)

		log("blockchain classname=", blockchain_classname,
			"=> is_nofetchblockchain=", is_nofetchblockchain)

		return is_nofetchblockchain
	} catch (e) {
		throw new Error(function_id + " : " + e)
	}
}

/**
 * Note on caching: If you want to optimise this Blockchain implementation,
 * set a cache:... to options passed in constructor
 * that way fetch will not refetch if it's in the cache already
 */
export default class NoFetchBlockchain extends Run.api.Blockchain {

	constructor(_options = {}, cache) {
		super()

		this.classname = NOFETCHBLOCKCHAIN_CLASSNAME // BEWARE this is used to check the class type in isNoFetchBlockchain

		this.DEBUG = true // set at true to log all calls to this blockchain
		const options = _options  // const to make sure we cannot update it

		if (cache) {
			this.cache = cache
			options.cache = cache
		}

		// to avoid the error "Blockchain mismatch" we must define .api, .apiKey & .network 
		// in the same way as we define them in the Run() object
		// const default_run_api = "run"    // deprecated since run_0.6.42
		const default_run_api = "whatsonchain"

		this.api = options.api || default_run_api

		this.retry_config = {
			retries: 3,
			minTimeout: 500,  // in ms
			maxTimeout: 5000,  // in ms
			factor: 2,  // exponential-backoff
		}

		this.blockchains = []
		this.options = options
		const log = (...args) => { console.log("NoFetchBlockchain: ", ...args) }
		this.log = log

		function get_options_fresh_copy() {
			var options_copy = { ...options } // Create a shallow copy to avoid circular structure issues
			if (cache) options_copy.cache = cache
			return options_copy
		}

		//
		// TokkenR API
		//
		this.blockchain_tokkenr = new TokkenrBlockchain()
		this.blockchains.push(this.blockchain_tokkenr)

		//
		// RUN API (deprecated)
		//
		// var options_copy = get_options_fresh_copy()
		// options_copy.api = default_run_api
		// this.blockchain_runapi = (new Run(options_copy)).blockchain
		// this.blockchains.push(this.blockchain_runapi)

		//
		// WhatsOnChain API
		//
		var options_copy = get_options_fresh_copy()
		options_copy.api = "whatsonchain"
		this.blockchain_whatsonchain = (new Run(options_copy)).blockchain
		this.blockchains.push(this.blockchain_whatsonchain)

		//
		// bsvBroadcast allows to broadcast to multiple endpoints
		//
		this.bsvBroadcaster = new BsvBroadcast({ DEBUG: true })

		// allow safety check in logs
		this.log_all_caches()
	}

	bsvBroadcaster = async function(rawtx_hex) {
		const function_id = "bsvBroadcaster(): "
		const log = (...args) => this.log(function_id, ...args)

		const { success, response } = await this.bsvBroadcaster.broadcast(rawtx_hex)
		if (!success) {
			throw new Error(function_id + "failed broadcast: success=" + success + ", response=" + response)
		}

		// extract txh
		const bsvtx = bsv.Transaction(rawtx_hex)
		const txh = bsvtx.hash

		log("SUCCESS broadcast: response=", response)
		return txh  // return should be txh if success
	}

	/**
	 * this is very useful to check if all caches are actually the same in memory
	 * We had a LOT of errors just because we had different caches and it messed up everything!
	 * especially the bsv purse balance. (28.02.2022)
	 */
	log_all_caches() {
		const log = (...args) => this.log("log_all_caches(): ", ...args)
		const all_caches = [this.cache, ...this.blockchains.map(e => e.cache)]
		let comparisons = []
		for (let i = 0; i < all_caches.length - 1; i++) {
			// '===' operator returns true only if same obj in memory
			comparisons.push(all_caches[i] === all_caches[i + 1])
		}
		log("are all caches the same pointer in memory ?", comparisons)
		if (isDevMode) log("all_caches=", all_caches)
	}

	get network() {
		/* from the doc: if the network starts with 'main', the Run SDK
			will use mainnet settings wherever it matters.
			For all other networks, Run will use testnet settings. */
		if (this.blockchain_runapi)
			return this.blockchain_runapi.network
		return "main" // default
	}

	async broadcast(rawtx) {
		return await retry(async () => {
			return await this._broadcast(rawtx)
		}, this.retry_config)
	}
	async _broadcast(rawtx) {
		const function_id = "CustomBlockchain: broadcast(): "
		const log = (...args) => this.log("broadcast(): ", ...args)
		if (this.DEBUG) log("DEBUG, rawtx=", rawtx)
		if (!is_string(rawtx)) throw new Error([
			function_id + "must be string, got rawtx:[", typeof rawtx, "]=",
			JSON.stringify(rawtx)].join(' ')
		)
		// our goal is to broadcast to ALL blockchains to increase reliability
		// and take the response from the FIRST one that fulfils it
		var allpromises = []
		this.blockchains.forEach(blockchain => {
			allpromises.push(blockchain.broadcast(rawtx))
		})
		allpromises.push(this.bsvBroadcaster(rawtx))
		var res_any
		try {
			//  broadcast to ALL and get the first that succeeds for speed
			var res = await promiseAny(allpromises)
			res_any = res[0]
		} catch (e) {
			console.error(function_id + "all blockchains failed for broadcasting rawtx=", rawtx,
				" res_any=", res_any)
			throw new Error([function_id + "all blockchains failed for", rawtx].join(' '))
		}
		// returned value should be a hex string which is the txid
		if (!is_string(res_any) || res_any.length === 0 || !is_hex(res_any)) {
			throw new Error([function_id + "res_any should be an hex string"
				+ " but got:", res_any].join(' '))
		}
		return String(res_any) // we return the first successful one
	}

	async fetch(txid, force = false) {
		const log = (...args) => this.log("fetch(): ", ...args)
		if (this.DEBUG) log("with txid=" + txid + " (" + typeof txid + "), force=" + force)

		if (!is_string(txid)) {
			// we have rare cases where we get txid = [object]: {}
			throw new Error([
				"CustomBlockchain: fetch(): must be string, got txid = [", typeof txid, "]:",
				JSON.stringify(txid)
			].join(' '))
		}
		// try loading from the cache first
		if (this.cache) {
			const txh = txid.split('_')[0]
			const rawtx = await this.cache.get("tx://" + txh)
			if (rawtx) {
				if (this.DEBUG) log("found in cache:", txh)
				return rawtx
			}
		}

		if (this.DEBUG) log("failed because this object doesn't fetch by design.")
		return null
	}

	filter_cache(filter_str) {
		const log = (...args) => this.log("filter_cache(): ", ...args)
		let cache = this.cache.cache
		if (this.DEBUG) log("cache=", cache)
		let keys = Object.keys(cache).filter(k => k.startsWith(filter_str))
		let res = {}
		for (let key of keys) {
			res[key] = cache[key]
		}
		if (this.DEBUG) log("filter_cache for '" + filter_str + "' res=", res)
		return res
	}

	search_in_cache(search_term) {
		const log = (...args) => this.log("search_in_cache(): ", ...args)
		let cache = this.cache.cache
		let res = cache[search_term]
		if (this.DEBUG) log("search_in_cache for '" + search_term + "' res=", res)
		return res
	}

	async utxos(script) {
		const log = (...args) => this.log("utxos(): ", ...args)
		if (this.DEBUG) log("with script=" + script)

		// do we need the jigs:// array for this ? probably not
		// but it could be easier to extract the address from the script and then filter jigs by owner
		// let all_jigs = this.filter_cache("jig://")
		let all_spends = this.filter_cache("spend://")
		let all_txs = this.filter_cache("tx://")

		// for all tx://, retrieve only the utxos, then for each output, check if the script matches
		let unspent_txs = []
		for (let tx in all_txs) {
			if (!all_spends[tx]) {  // if unspent
				unspent_txs.push(tx)
			}
		}
		// log("found unspent_txs=",unspent_txs)

		let valid_utxos = []
		for (let tx_index of unspent_txs) {
			// TODO all outputs & check if the script matches
			let rawtx = all_txs[tx_index]
			// log("tx=",tx_index,rawtx)
			let bsvtx = bsv.Transaction(rawtx)
			let outputs = bsvtx.outputs
			// log("outputs=",outputs)
			for (let i = 0; i < outputs.length; i++) {
				let output = outputs[i]
				// log("script=",script,"\nVS\n",output.script.toHex())
				if (script == output.script.toHex()) {
					valid_utxos.push({
						txid: tx_index.split("tx://")[1],
						vout: i,
						satoshis: output.satoshis,
						script: output.script,
					})
				}
			}
		}

		if (this.DEBUG) log("valid_utxos=", valid_utxos)
		if (this.DEBUG) log("found", valid_utxos.length, "valid utxos - only from cache because this object doesn't fetch by design.")
		return []  // we need to return empty array if there is no utxos for that address
	}

	async time(txid) {
		const log = (...args) => this.log("time(): ", ...args)
		if (this.DEBUG) log("with txid=" + txid)
		if (!is_string(txid)) {
			throw new Error(["CustomBlockchain: time(): must be string, got:", txid].join(' '))
		}

		// try loading from the cache first
		if (this.cache) {
			let tx_time = await this.cache.get("time://" + txid)
			if (!tx_time) {
				const txhash = txid.split('_')[0]
				tx_time = await this.cache.get("time://" + txhash)
			}
			if (tx_time) {
				if (this.DEBUG) log("found in cache: ", tx_time)
				return tx_time
			}
		}

		if (this.DEBUG) log("failed because this object doesn't fetch by design.")
		return null
	}

	/* from the doc:
		"If the Blockchain API does not support spends, it may return a 
		Run.error.NotImplementedError, however, Run will only partially work in this case."
		=> but here we want to simulate as-if it was working, so we should not throw an error
	*/
	async spends(txid, vout) {
		const log = (...args) => this.log("spends(): ", ...args)
		if (this.DEBUG) log("with txid=" + txid + ", vout=" + vout)
		if (!is_string(txid)) {
			throw new Error(["CustomBlockchain: spends(): must be string, got:", txid].join(' '))
		}

		// try loading from the cache first
		txid = txid.split('_')[0] // safety check
		let search_cache = "spend://" + txid + "_o" + vout
		const spend_txid = this.search_in_cache(search_cache)

		if (this.DEBUG) log("spend_txid: ", spend_txid)

		if (spend_txid) return spend_txid
		else return null  // null for unspent
	}
}
